import { Measurement } from '@kentik/ui-shared/query/query.types';
import { titleExactMatches } from './metricsTreeData';

export interface SubCategory {
  /** Full path prefix, e.g. '/metrics/interfaces' */
  key: string;
  /** Human-readable label, e.g. 'Interfaces' */
  label: string;
  /** The parent category prefix, e.g. '/metrics' */
  parentKey: string;
  /** Number of measurements under this sub-category */
  measurementCount: number;
  /** Nested sub-categories (deeper levels) */
  children?: SubCategory[];
  /** True when this sub-category's key is itself a valid measurement path (not just an intermediate grouping) */
  isMeasurement?: boolean;
}

/**
 * Extract first-level sub-categories from measurements for a given category prefix.
 *
 * For example, given prefix='/metrics' and measurements like:
 *   /metrics/interfaces/counters, /metrics/system, /metrics/system/cpus
 * Returns: [{ key: '/metrics/interfaces', label: 'Interfaces' }, { key: '/metrics/system', label: 'System' }]
 *
 * If there are no sub-categories (e.g. the category only has a root measurement),
 * returns an empty array — signaling the UI should skip the sub-category step.
 */
function extractSubCategories(measurements: Measurement[], categoryPrefix: string): SubCategory[] {
  const subCatMap = new Map<string, { label: string; count: number }>();

  measurements.forEach((m) => {
    const path = m.measurement;
    if (
      !path ||
      !path.startsWith(categoryPrefix) ||
      (path.length > categoryPrefix.length && path[categoryPrefix.length] !== '/')
    ) {
      return;
    }

    // Get the remainder after the prefix: '/metrics/interfaces/counters' → '/interfaces/counters'
    const remainder = path.slice(categoryPrefix.length);

    // If the measurement IS the category root (e.g. '/traffic'), skip it for sub-category extraction
    if (!remainder || remainder === '/') {
      return;
    }

    // Extract first segment: '/interfaces/counters' → 'interfaces'
    const segments = remainder.split('/').filter(Boolean);
    if (segments.length === 0) {
      return;
    }

    const firstSegment = segments[0];
    const subKey = `${categoryPrefix}/${firstSegment}`;

    const existing = subCatMap.get(subKey);
    if (existing) {
      existing.count += 1;
    } else {
      const label = titleExactMatches[firstSegment] || firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1);
      subCatMap.set(subKey, { label, count: 1 });
    }
  });

  // Sort alphabetically by label
  return Array.from(subCatMap.entries())
    .map(([key, { label, count }]) => ({
      key,
      label,
      parentKey: categoryPrefix,
      measurementCount: count
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Extract sub-categories recursively up to `maxDepth` additional levels.
 *
 * For example, with prefix='/metrics' and maxDepth=2:
 *   /metrics/interfaces/counters → Interfaces > Counters
 *   /metrics/system/cpus         → System > CPUs
 *   /metrics/protocols/bgp/neighbors → Protocols > BGP > Neighbors
 *
 * Edge-case: when a measurement path is also a prefix of other measurements
 * (e.g. /metrics/kentik/agent/nms/snmp AND /metrics/kentik/agent/nms/snmp/sources),
 * the parent becomes a group node. To keep the parent measurement selectable, a
 * synthetic leaf child is prepended so the user can still pick it.
 */

function extractSubCategoriesDeepInner(
  measurements: Measurement[],
  measurementPaths: Set<string>,
  displayNameMap: Map<string, string | undefined>,
  categoryPrefix: string,
  maxDepth: number
): SubCategory[] {
  const subs = extractSubCategories(measurements, categoryPrefix);

  return subs.map((sub) => {
    const isRealMeasurement = measurementPaths.has(sub.key);

    // If this sub-category IS a measurement and has no other measurements beneath it, it's a leaf.
    // (measurementCount === 1 means only itself exists at this prefix)
    if (isRealMeasurement && sub.measurementCount === 1) {
      return sub;
    }

    // Always recurse if the sub-category is NOT an actual measurement — we need to
    // drill down until we reach real measurement leaves, regardless of maxDepth.
    // Also recurse when a measurement has deeper measurements beneath it (count > 1)
    // so that those children are represented in the tree.
    // When maxDepth > 0, recurse normally (decrement depth).
    const shouldRecurse = maxDepth > 0 || !isRealMeasurement || sub.measurementCount > 1;

    if (!shouldRecurse) {
      return sub;
    }

    const children = extractSubCategoriesDeepInner(
      measurements,
      measurementPaths,
      displayNameMap,
      sub.key,
      Math.max(maxDepth - 1, 0)
    );

    if (children.length >= 1) {
      // This sub-category has deeper children. If the sub-category's key is ALSO
      // an actual measurement, add a synthetic leaf child so it remains selectable.
      if (isRealMeasurement) {
        const displayName = displayNameMap.get(sub.key);
        const selfChild: SubCategory = {
          key: sub.key,
          label: displayName || sub.label,
          parentKey: sub.key,
          measurementCount: 1,
          isMeasurement: true
        };
        return { ...sub, children: [selfChild, ...children] };
      }

      return { ...sub, children };
    }

    return sub;
  });
}

/**
 * Collapse chains of single-child intermediate group nodes into a single node
 * with a slash-joined label.
 *
 * For example:
 *   Kentik → Agent → NMS → Snmp (with children)
 * becomes:
 *   Kentik/Agent/NMS/Snmp (with children)
 *
 * If a collapsed group ends up with exactly one leaf child, the leaf is absorbed
 * entirely to produce a single selectable node with a slash-joined compound label.
 *
 * Leaf nodes (no children) and nodes with multiple children are never collapsed.
 * Synthetic measurement leaves (isMeasurement) are also never collapsed.
 */
function collapseSubCategory(sub: SubCategory): SubCategory {
  // Leaf node or synthetic measurement leaf — nothing to collapse
  if (!sub.children || sub.children.length === 0 || sub.isMeasurement) {
    return sub;
  }

  // Recursively collapse children first
  let collapsed: SubCategory = { ...sub, children: sub.children.map((child) => collapseSubCategory(child)) };

  // Merge single-child chains: if this intermediate node has exactly one child
  // and that child is also an intermediate node (has children, is not a leaf measurement)
  while (
    collapsed.children &&
    collapsed.children.length === 1 &&
    collapsed.children[0].children &&
    collapsed.children[0].children.length > 0 &&
    !collapsed.children[0].isMeasurement
  ) {
    const child = collapsed.children[0];
    collapsed = {
      ...collapsed,
      key: child.key,
      label: `${collapsed.label}/${child.label}`,
      children: child.children,
      measurementCount: child.measurementCount
    };
  }

  // Final step: if after collapsing chains we have exactly one child that is a leaf
  // (no children of its own), absorb it entirely — the group becomes a single leaf
  // with a compound label. This avoids a group node that only has one selectable item.
  if (
    collapsed.children &&
    collapsed.children.length === 1 &&
    !collapsed.children[0].children &&
    !collapsed.children[0].isMeasurement
  ) {
    const child = collapsed.children[0];
    collapsed = {
      ...collapsed,
      key: child.key,
      label: `${collapsed.label}/${child.label}`,
      children: undefined,
      measurementCount: child.measurementCount
    };
  }

  return collapsed;
}

function collapseSubCategories(subs: SubCategory[]): SubCategory[] {
  return subs.map((sub) => collapseSubCategory(sub));
}

export function extractSubCategoriesDeep(
  measurements: Measurement[],
  categoryPrefix: string,
  maxDepth: number = 2
): SubCategory[] {
  // Build a set of all measurement paths for O(1) lookup
  const measurementPaths = new Set(measurements.map((m) => m.measurement).filter(Boolean));
  // Build a map of measurement path → displayName for O(1) lookup during recursion
  const displayNameMap = new Map<string, string | undefined>(
    measurements.filter((m) => m.measurement).map((m) => [m.measurement, m.displayName])
  );

  const uncollapsedTree = extractSubCategoriesDeepInner(
    measurements,
    measurementPaths,
    displayNameMap,
    categoryPrefix,
    maxDepth
  );
  return collapseSubCategories(uncollapsedTree);
}
